import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createBlock as _createBlock, createStaticVNode as _createStaticVNode } from "vue"

const _hoisted_1 = { class: "mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm" }
const _hoisted_2 = { class: "flex flex-col gap-4" }
const _hoisted_3 = /*#__PURE__*/_createStaticVNode("<div class=\"flex flex-col\"><div class=\"flex items-center gap-2\"><h3 class=\"text-sm font-semibold text-gray-900\">Automatic phase progression</h3><span class=\"rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium uppercase tracking-wide text-gray-600\"> Internal admin </span></div><p class=\"mt-1 text-sm text-gray-500\"> When a learner masters every concept of every thread on their assessment level, move them to the next level without waiting for an admin. The manual bump under Edit Learner keeps working either way. </p></div>", 1)
const _hoisted_4 = { class: "flex flex-col gap-2" }
const _hoisted_5 = {
  key: 0,
  class: "h-2 w-2 rounded-full bg-blue-600"
}
const _hoisted_6 = { class: "flex flex-col" }
const _hoisted_7 = {
  key: 0,
  class: "text-xs text-gray-500"
}
const _hoisted_8 = {
  key: 0,
  class: "text-xs text-gray-500"
}
const _hoisted_9 = /*#__PURE__*/_createElementVNode("p", { class: "text-xs text-gray-500" }, " A learner is only moved on their next submission, and a move archives the assessment they finished and starts the next level from zero — exactly as the manual bump does. ", -1)
const _hoisted_10 = {
  key: 0,
  class: "mt-3 text-sm text-red-600"
}
const _hoisted_11 = {
  key: 1,
  class: "mt-3 text-sm text-green-600"
}

import { computed, ref, watch } from "vue";
import { RadioGroup, RadioGroupLabel, RadioGroupOption } from "@headlessui/vue";
import { useSchoolStore } from "@/stores/schoolStore";
import { useFilterStore } from "@/stores/filterStore";
import { School } from "@/interfaces/school.interface";
import { showToast } from "@/utils/toastUtils";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/vue/20/solid";

/**
 * Per-school automatic phase progression (NLM-Test #95).
 *
 * Unlike the Freshchat and Grammar cards this is a THREE-state control, not a
 * switch. The learner API resolves the setting school-first and only falls
 * through to the school group when the school's own value is neither true nor
 * false (reflective-learning-api/src/server/services/phase-progression.js,
 * resolveAutoPhaseProgression). A two-state switch would write `false` the
 * first time it was touched and permanently pin the school away from its
 * group, which is the opposite of what the group-level control is for.
 *
 * "Inherit" is sent as `null`. The server declares the leaf as
 * Joi.boolean().allow(null) precisely so this state is reachable.
 */
type SettingValue = "inherit" | "on" | "off";


export default /*#__PURE__*/_defineComponent({
  __name: 'PhaseProgressionSettingsCard',
  setup(__props) {

const schoolStore = useSchoolStore();
const filterStore = useFilterStore();

const loading = ref(false);
const errorMessage = ref("");
const successMessage = ref("");

const currentSchool = computed<School | null>(() => {
  const school = schoolStore.getCurrentSchool;
  return school ? (school as School) : null;
});

/**
 * The group's value, but only when we can prove the group in the filter bar is
 * this school's group. Mission Control's filter selection and the school being
 * edited normally agree, and when they do not, telling the admin what "inherit"
 * resolves to would be a guess presented as fact.
 */
const schoolGroupSetting = computed<boolean | null>(() => {
  const school = currentSchool.value;
  const group = filterStore.selectedSchoolGroup;
  if (!school || !group || !school.schoolGroup) return null;
  if (String(school.schoolGroup) !== String(group._id)) return null;

  // Undefined and false both mean off at group level: the group has nothing
  // above it to inherit from.
  return (group.settings || {}).autoPhaseProgressionEnabled === true;
});

const inheritHint = computed(() => {
  if (!currentSchool.value) return "";
  if (!currentSchool.value.schoolGroup) {
    return "This school is not in a school group, so this means off.";
  }
  if (schoolGroupSetting.value === null) {
    return "Whatever the school group is set to applies.";
  }
  return schoolGroupSetting.value
    ? "The school group currently has it on."
    : "The school group currently has it off.";
});

const options = computed(() => [
  {
    value: "inherit" as SettingValue,
    label: "Use the school group's setting",
    hint: inheritHint.value
  },
  {
    value: "on" as SettingValue,
    label: "On for this school",
    hint: ""
  },
  {
    value: "off" as SettingValue,
    label: "Off for this school",
    hint: "Never, even when the school group has it on."
  }
]);

/**
 * `=== true` / `=== false`: a school that predates the field, or one whose
 * override has been cleared, has no value here and inherits.
 */
const toSettingValue = (stored: unknown): SettingValue =>
  stored === true ? "on" : stored === false ? "off" : "inherit";

const toPayloadValue = (value: SettingValue): boolean | null =>
  value === "on" ? true : value === "off" ? false : null;

const localValue = ref<SettingValue>("inherit");

watch(
  currentSchool,
  (school) => {
    localValue.value = toSettingValue(school?.settings?.autoPhaseProgressionEnabled);
  },
  { immediate: true }
);

const SUCCESS_TEXT: Record<SettingValue, string> = {
  on: "Automatic phase progression switched on for this school.",
  off: "Automatic phase progression switched off for this school.",
  inherit: "This school now follows its school group."
};

const onSelect = async (value: SettingValue) => {
  if (loading.value || value === localValue.value) return;

  const schoolId = currentSchool.value?._id || filterStore.selectedFilters.school || "";
  if (!schoolId) {
    errorMessage.value = "Select a school to update automatic phase progression.";
    return;
  }

  const previousValue = localValue.value;
  localValue.value = value;
  loading.value = true;
  errorMessage.value = "";
  successMessage.value = "";

  const result = await schoolStore.updateSchool(schoolId, {
    settings: { autoPhaseProgressionEnabled: toPayloadValue(value) }
  } as Partial<School>);

  loading.value = false;

  if (!result) {
    localValue.value = previousValue;
    errorMessage.value =
      "Unable to update automatic phase progression. Please try again.";
    showToast({
      text: errorMessage.value,
      type: "error",
      icon: ExclamationTriangleIcon
    });
    return;
  }

  const refreshed = schoolStore.getCurrentSchool as School | null;
  // The server strips this field for non-internal admins and still answers
  // 200, so trust the response rather than the value we asked for.
  localValue.value = toSettingValue(refreshed?.settings?.autoPhaseProgressionEnabled);

  // Mirror into filterStore, the copy populated at login, as the Freshchat and
  // Grammar cards do. Without it the two copies of settings diverge.
  if (refreshed?.settings && refreshed._id) {
    filterStore.updateSchoolSettings(refreshed._id, refreshed.settings);
  }

  if (localValue.value !== value) {
    errorMessage.value =
      "That change was not applied. Automatic phase progression can only be changed by an internal admin.";
    showToast({
      text: errorMessage.value,
      type: "error",
      icon: ExclamationTriangleIcon
    });
    return;
  }

  successMessage.value = SUCCESS_TEXT[value];
  showToast({
    text: successMessage.value,
    type: "success",
    icon: CheckCircleIcon
  });
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _hoisted_3,
      _createVNode(_unref(RadioGroup), {
        "model-value": localValue.value,
        disabled: loading.value || !currentSchool.value,
        "onUpdate:modelValue": onSelect
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(RadioGroupLabel), { class: "sr-only" }, {
            default: _withCtx(() => [
              _createTextVNode(" Automatic phase progression for this school ")
            ]),
            _: 1
          }),
          _createElementVNode("div", _hoisted_4, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(options.value, (option) => {
              return (_openBlock(), _createBlock(_unref(RadioGroupOption), {
                key: option.value,
                value: option.value,
                as: "template"
              }, {
                default: _withCtx(({ checked }) => [
                  _createElementVNode("div", {
                    class: _normalizeClass([
                'flex items-start gap-2 text-sm focus:outline-none',
                loading.value || !currentSchool.value ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
              ])
                  }, [
                    _createElementVNode("span", {
                      "aria-hidden": "true",
                      class: _normalizeClass([
                  checked ? 'border-blue-600' : 'border-gray-300',
                  'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border bg-white'
                ])
                    }, [
                      checked
                        ? (_openBlock(), _createElementBlock("span", _hoisted_5))
                        : _createCommentVNode("", true)
                    ], 2),
                    _createElementVNode("span", _hoisted_6, [
                      _createElementVNode("span", {
                        class: _normalizeClass([checked ? 'text-gray-900' : 'text-gray-700', 'font-medium'])
                      }, _toDisplayString(option.label), 3),
                      (option.hint)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(option.hint), 1))
                        : _createCommentVNode("", true)
                    ])
                  ], 2)
                ]),
                _: 2
              }, 1032, ["value"]))
            }), 128))
          ])
        ]),
        _: 1
      }, 8, ["model-value", "disabled"]),
      (!currentSchool.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_8, " Select a school to change this. "))
        : _createCommentVNode("", true),
      _hoisted_9
    ]),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(errorMessage.value), 1))
      : (successMessage.value)
        ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(successMessage.value), 1))
        : _createCommentVNode("", true)
  ]))
}
}

})